import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * ErrorBoundary - Catches React runtime errors and prevents full app crashes.
 * 
 * This is critical for HFT applications where the UI must remain responsive
 * even if a component encounters an error during rapid data updates.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console for debugging
    console.error('ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo?.componentStack);
    
    this.setState({ errorInfo });
    
    // Squelch performance-related errors (DataCloneError, etc.)
    // These are typically non-fatal and caused by React DevTools
    if (error?.name === 'DataCloneError' || 
        error?.message?.includes('out of memory') ||
        error?.message?.includes('DataClone')) {
      console.warn('Performance monitoring error squelched. App will continue.');
      // Auto-retry after squelching performance errors
      setTimeout(() => this.handleRetry(), 1000);
    }
  }

  handleRetry = () => {
    this.setState(prev => ({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: prev.retryCount + 1
    }));
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { error, retryCount } = this.state;
      const isMemoryError = error?.message?.includes('memory') || 
                           error?.name === 'DataCloneError';
      
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-lg w-full bg-surface border border-border rounded-lg p-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-sell/10 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-sell" />
              </div>
            </div>
            
            <h1 className="font-heading font-bold text-xl text-foreground mb-2">
              {isMemoryError ? 'Performance Issue Detected' : 'Something went wrong'}
            </h1>
            
            <p className="text-muted-foreground text-sm mb-4">
              {isMemoryError 
                ? 'The application encountered a memory issue during rendering. This is typically caused by large data sets.'
                : 'An unexpected error occurred. Please try refreshing the page.'}
            </p>
            
            {error && (
              <div className="bg-background border border-border rounded p-3 mb-4 text-left">
                <p className="text-xs text-sell font-mono break-all">
                  {error.name}: {error.message?.substring(0, 200)}
                </p>
              </div>
            )}
            
            <div className="flex gap-3 justify-center">
              {retryCount < 3 && (
                <button
                  onClick={this.handleRetry}
                  className="flex items-center gap-2 px-4 py-2 bg-accent text-background rounded hover:bg-accent/90 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Retry ({3 - retryCount} left)
                </button>
              )}
              
              <button
                onClick={this.handleRefresh}
                className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded hover:bg-muted/80 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh Page
              </button>
            </div>
            
            {retryCount >= 3 && (
              <p className="text-xs text-muted-foreground mt-4">
                Multiple retries failed. Please refresh the page or contact support.
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
